import { MARKS, BLOCKS, INLINES } from '@contentful/rich-text-types';
import type { Options } from '@contentful/rich-text-react-renderer';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type {
  Document,
  TopLevelBlock,
  Block,
  Inline,
} from '@contentful/rich-text-types';
import type { ReactNode } from 'react';

import { TextLink } from '../../TextLink';
import {
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
} from '../../../Text';
import { AdditionalInfo } from '../../AdditionalInfo';
import { EmbeddedFaqItem } from '../../EmbeddedFaqItem';
import { InfoSnippetLong } from '../../InfoSnippetLong';
import {
  extractFaqHeadingLevelFromTarget,
  extractFaqItemFromTarget,
} from '../extractEmbeddedFaqItems';
import {
  CTA_LINK_CONTENT_TYPE,
  extractCtaLinkFromTarget,
} from '../extractEmbeddedCtaLink';
import type { RenderCtaLink } from '../extractEmbeddedCtaLink';
import { HasTextStyleRules } from '../rules';
import {
  BoldWrapper,
  ItalicWrapper,
  UnderlineWrapper,
  EmbeddedWrapper,
  GridCell,
  CTA_LINK_CLASS_NAME,
} from './ContentfulRichText.styles';

const wrapNode = (
  children: ReactNode,
  rules?: HasTextStyleRules,
  isSidebar = false,
  isFullWidth = false,
  type = 'main',
  gridRow?: number,
) => (
  <GridCell
    isSidebar={isSidebar}
    isFullWidth={isFullWidth}
    textStyle={rules?.textStyle}
    className={isSidebar ? 'grid-cell-sidebar' : `grid-cell-${type}`}
    gridRow={gridRow}
  >
    {children}
  </GridCell>
);

const overrideStylesForHeadings = { marginBlock: '1rem 0' };

type AlignmentMaps = {
  paragraphRows: WeakMap<TopLevelBlock, number>;
  snippetRows: WeakMap<TopLevelBlock, number>;
  otherRows: WeakMap<TopLevelBlock, number>;
};

const emptyMaps = (): AlignmentMaps => ({
  paragraphRows: new WeakMap<TopLevelBlock, number>(),
  snippetRows: new WeakMap<TopLevelBlock, number>(),
  otherRows: new WeakMap<TopLevelBlock, number>(),
});

const buildAlignmentMap = (nodes: TopLevelBlock[]): AlignmentMaps => {
  const paragraphRows = new WeakMap<TopLevelBlock, number>();
  const snippetRows = new WeakMap<TopLevelBlock, number>();
  const otherRows = new WeakMap<TopLevelBlock, number>();

  let gridRow = 0;
  let contiguousRunStart: number | null = null;

  nodes.forEach(node => {
    const contentTypeId = node.data?.target?.sys?.contentType?.sys?.id;
    const isSnippet =
      node.nodeType === BLOCKS.EMBEDDED_ENTRY &&
      contentTypeId === 'infoSnippet';

    if (node.nodeType === BLOCKS.PARAGRAPH) {
      gridRow += 1;
      paragraphRows.set(node, gridRow);
      if (contiguousRunStart === null) {
        contiguousRunStart = gridRow;
      }
    } else if (isSnippet) {
      snippetRows.set(node, contiguousRunStart ?? gridRow);
    } else {
      gridRow += 1;
      otherRows.set(node, gridRow);
      contiguousRunStart = null;
    }
  });

  return { paragraphRows, snippetRows, otherRows };
};

export const createOptions = (
  enableExtraction = false,
  rules?: HasTextStyleRules,
  document?: Document,
  renderCtaLink?: RenderCtaLink,
): Options => {
  let headingCount = 0;

  const { paragraphRows, snippetRows, otherRows } =
    enableExtraction && document
      ? buildAlignmentMap(document.content as TopLevelBlock[])
      : emptyMaps();

  const asTopLevel = (node: Block | Inline) => node as unknown as TopLevelBlock;

  return {
    renderMark: {
      [MARKS.BOLD]: text => <BoldWrapper>{text}</BoldWrapper>,
      [MARKS.ITALIC]: text => <ItalicWrapper>{text}</ItalicWrapper>,
      [MARKS.UNDERLINE]: text => <UnderlineWrapper>{text}</UnderlineWrapper>,
    },

    renderNode: {
      [INLINES.HYPERLINK]: ({ data }, children) => {
        const isAnchorLink = data.uri?.includes('#content-headline-');
        return (
          <TextLink
            href={data.uri}
            variant="text"
            {...(!isAnchorLink && {
              target: '_blank',
              rel: 'noopener noreferrer',
            })}
          >
            {children}
          </TextLink>
        );
      },

      [BLOCKS.PARAGRAPH]: (node, children) =>
        wrapNode(
          <p>{children}</p>,
          rules,
          false,
          false,
          'p',
          paragraphRows.get(asTopLevel(node)),
        ),

      [BLOCKS.HEADING_2]: (node, children) => {
        headingCount += 1;
        return wrapNode(
          <Heading2
            id={`content-headline-${headingCount}`}
            textColor="current"
            overrideStyles={overrideStylesForHeadings}
          >
            {children}
          </Heading2>,
          rules,
          false,
          true,
          'h2',
          otherRows.get(asTopLevel(node)),
        );
      },

      [BLOCKS.HEADING_3]: (node, children) =>
        wrapNode(
          <Heading3
            textColor="current"
            overrideStyles={overrideStylesForHeadings}
          >
            {children}
          </Heading3>,
          rules,
          false,
          true,
          'h3',
          otherRows.get(asTopLevel(node)),
        ),

      [BLOCKS.HEADING_4]: (node, children) =>
        wrapNode(
          <Heading4
            textColor="current"
            overrideStyles={overrideStylesForHeadings}
          >
            {children}
          </Heading4>,
          rules,
          false,
          true,
          'h4',
          otherRows.get(asTopLevel(node)),
        ),

      [BLOCKS.HEADING_5]: (node, children) =>
        wrapNode(
          <Heading5
            textColor="current"
            overrideStyles={overrideStylesForHeadings}
          >
            {children}
          </Heading5>,
          rules,
          false,
          true,
          'h5',
          otherRows.get(asTopLevel(node)),
        ),

      [BLOCKS.HEADING_6]: (node, children) =>
        wrapNode(
          <Heading6
            textColor="current"
            overrideStyles={overrideStylesForHeadings}
          >
            {children}
          </Heading6>,
          rules,
          false,
          true,
          'h6',
          otherRows.get(asTopLevel(node)),
        ),

      [BLOCKS.EMBEDDED_ENTRY]: node => {
        const { target } = node.data;
        const contentTypeId = target?.sys?.contentType?.sys?.id;

        if (!target?.fields) return null;

        if (contentTypeId === 'infoSnippetLong') {
          const content = documentToReactComponents(
            target.fields.text,
            createOptions(false, rules, undefined, renderCtaLink),
          );

          return wrapNode(
            <InfoSnippetLong color={target.fields.color}>
              {content}
            </InfoSnippetLong>,
            rules,
            false,
            false,
            'main',
            otherRows.get(asTopLevel(node)),
          );
        }

        if (contentTypeId === 'infoSnippet') {
          return wrapNode(
            <AdditionalInfo>{target.fields.text}</AdditionalInfo>,
            rules,
            enableExtraction,
            false,
            'main',
            snippetRows.get(asTopLevel(node)),
          );
        }

        if (contentTypeId === 'faqItem') {
          const faq = extractFaqItemFromTarget(target);
          if (faq?.answer.type !== 'contentful') return null;

          const answer = documentToReactComponents(
            faq.answer.text,
            createOptions(false, rules, undefined, renderCtaLink),
          );

          return wrapNode(
            <EmbeddedFaqItem
              question={faq.question}
              headingLevel={extractFaqHeadingLevelFromTarget(target)}
            >
              {answer}
            </EmbeddedFaqItem>,
            rules,
            false,
            false,
            'main',
            otherRows.get(asTopLevel(node)),
          );
        }

        if (contentTypeId === CTA_LINK_CONTENT_TYPE) {
          const link = extractCtaLinkFromTarget(target);
          if (!link || !renderCtaLink) return null;

          return wrapNode(
            <div className={CTA_LINK_CLASS_NAME}>{renderCtaLink(link)}</div>,
            rules,
            false,
            false,
            'main',
            otherRows.get(asTopLevel(node)),
          );
        }

        return wrapNode(
          <EmbeddedWrapper
            dangerouslySetInnerHTML={{ __html: target.fields.embedded }}
          />,
          rules,
          false,
          false,
          'main',
          otherRows.get(asTopLevel(node)),
        );
      },

      [BLOCKS.UL_LIST]: (node, children) =>
        wrapNode(
          <ul>{children}</ul>,
          rules,
          false,
          false,
          'ul',
          otherRows.get(asTopLevel(node)),
        ),

      [BLOCKS.OL_LIST]: (node, children) =>
        wrapNode(
          <ol>{children}</ol>,
          rules,
          false,
          false,
          'ol',
          otherRows.get(asTopLevel(node)),
        ),

      [BLOCKS.TABLE]: (node, children) =>
        wrapNode(
          <table>
            <tbody>{children}</tbody>
          </table>,
          rules,
          false,
          false,
          'table',
          otherRows.get(asTopLevel(node)),
        ),

      [BLOCKS.LIST_ITEM]: (_, children) => <li>{children}</li>,
      [BLOCKS.TABLE_ROW]: (_, children) => <tr>{children}</tr>,
      [BLOCKS.TABLE_CELL]: (_, children) => <td>{children}</td>,
      [BLOCKS.TABLE_HEADER_CELL]: (_, children) => <th>{children}</th>,
    },
  };
};
